/**
 * Shared, framework-light theme primitives — the SkySpeaks-wide design-system
 * layer (see `_docs/architecture/theme-system.md`). No Vue, no reactivity. The
 * one DOM touch (`applyThemeToDocument`) is guarded so this module is safe to
 * import in a Node test environment.
 *
 * `theme.mode` has two legitimate sources depending on auth context
 * (`useTheme` + `useAppearanceSettings`); this module only knows how to
 * normalize a mode, resolve it to light/dark, and read/write the first-paint
 * cache (Cache A).
 */

export const THEME_MODES = Object.freeze(['light', 'dark', 'system']);

/** Cache A — browser/device first-paint theme cache. `{ mode }` only. */
export const THEME_CACHE_KEY = 'skyspeaks.theme';

/**
 * First-frame background per resolved theme. MUST equal the `--page-bg` token
 * values in `tailwind.css`. The inline bootstrap in `index.html` hard-codes the
 * same two hexes — if the dark value changes, update BOTH (a source-pattern
 * test asserts the bootstrap matches).
 */
export const THEME_FIRST_PAINT_BG = Object.freeze({
  light: '#ffffff',
  dark: '#111113',
});

/** Anything not an exact known mode string -> `'system'`. */
export function normalizeThemeMode(value) {
  return THEME_MODES.includes(value) ? value : 'system';
}

/**
 * Resolve a mode to a concrete `'light' | 'dark'`.
 *   - gate closed (`released === false`) -> always `'light'`
 *   - `'system'` -> `systemPrefersDark ? 'dark' : 'light'`
 *   - otherwise the (normalized) mode itself
 */
export function resolveTheme(mode, systemPrefersDark, released) {
  if (!released) return 'light';
  const m = normalizeThemeMode(mode);
  if (m === 'system') return systemPrefersDark ? 'dark' : 'light';
  return m;
}

/** Read the mode from Cache A. Missing / malformed / unknown -> `'system'`. Never throws. */
export function readThemeModeFromCache() {
  try {
    const raw = localStorage.getItem(THEME_CACHE_KEY);
    if (!raw) return 'system';
    const parsed = JSON.parse(raw);
    return normalizeThemeMode(parsed && parsed.mode);
  } catch {
    return 'system';
  }
}

/** Write `{ mode }` to Cache A. Never throws (quota / private mode). */
export function writeThemeModeToCache(mode) {
  try {
    localStorage.setItem(THEME_CACHE_KEY, JSON.stringify({ mode: normalizeThemeMode(mode) }));
  } catch {
    /* ignore */
  }
}

/** `true` if the OS currently prefers dark. Guarded for non-browser / no-matchMedia. */
export function systemPrefersDark() {
  try {
    return (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
    );
  } catch {
    return false;
  }
}

/**
 * Apply a resolved theme to `<html>`: `data-theme`, the mirrored `.dark` class
 * (Tailwind 3.3.3 keys `dark:` off the class), `color-scheme`, and the
 * first-frame background. Reserved attributes (`data-palette` / `data-contrast`)
 * are never touched. No-op outside a browser.
 */
export function applyThemeToDocument(resolved) {
  if (typeof document === 'undefined' || !document.documentElement) return;
  const dark = resolved === 'dark';
  const el = document.documentElement;
  el.dataset.theme = dark ? 'dark' : 'light';
  el.classList.toggle('dark', dark);
  el.style.colorScheme = dark ? 'dark' : 'light';
  el.style.backgroundColor = dark ? THEME_FIRST_PAINT_BG.dark : THEME_FIRST_PAINT_BG.light;
}
